import React, { useCallback, useState } from "react";
import moment from "moment";

import {
  TableRow,
  TableCell,
  Table,
  TableContainer,
  TableBody,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  IconButton,
} from "@material-ui/core";

import { EnhancedTableHead } from "./index";

import excelIcon from "../../../../assets/images/icons/excel.svg";
import axios from "../../../../axios";

const headCells = [
  {
    id: "i",
    numeric: false,
    disablePadding: false,
    label: "Sl.No",
  },
  {
    id: "company",
    numeric: false,
    disablePadding: false,
    label: "Company Name",
  },
  {
    id: "paymentRef",
    numeric: false,
    disablePadding: false,
    label: "Payment Ref",
  },
  {
    id: "paymentDate",
    numeric: false,
    disablePadding: false,
    label: "Payment Date",
  },
  {
    id: "totalAmount",
    numeric: false,
    disablePadding: false,
    label: "Total Amount (£)",
  },
  {
    id: "refundAmount",
    numeric: false,
    disablePadding: false,
    label: "Refund Amount (£)",
  },
  {
    id: "refundDate",
    numeric: false,
    disablePadding: false,
    label: "Refund Date",
  },
  {
    id: "notes",
    numeric: false,
    disablePadding: false,
    label: "Notes",
  },
];

const styles = {
  fontSize: "13px",
  padding: "6px",
};

const CardItem = ({
  title,
  active,
  classes,
  data,
  isLoading,
  matches,
  hasData,
  values,
}) => {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("company");
  const [downloading, setDownloading] = useState(false);

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order],
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = data.map((n) => n.name);
        setSelected(newSelecteds);
        return;
      }

      setSelected([]);
    },
    [data],
  );

  const downloadHandler = async () => {
    try {
      setDownloading(true);

      let query = "";

      if (values.companyId) {
        query += `companyId=${values.companyId}`;
      }

      if (values.businessType) {
        query += `${query ? "&" : ""}businessType=${values.businessType}`;
      }

      if (values.fromDate) {
        query += `${query ? "&" : ""}fromDate=${values.fromDate}`;
      }

      if (values.toDate) {
        query += `${query ? "&" : ""}toDate=${values.toDate}`;
      }

      const response = await axios.get(
        `/api/v1/payments/refunds/download?${query}`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute(
        "download",
        `Refund_Statement_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className={active}>
      <CardContent style={{ padding: "8px 6px" }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          style={{ padding: "6px" }}
        >
          <Grid item xs>
            <Typography variant="body1" noWrap>
              {title}
            </Typography>
          </Grid>

          {hasData && (
            <Grid item>
              <Tooltip title="Download Excel">
                <span>
                  <IconButton
                    size="medium"
                    onClick={downloadHandler}
                    disabled={downloading || data.length === 0}
                  >
                    <img
                      src={excelIcon}
                      alt="excel"
                      style={{
                        width: 25,
                        height: 25,
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <TableContainer
          className={matches ? classes.container2 : classes.container}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            className={classes.table}
            size="small"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              headCells={headCells}
            />

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center" }}
                    rowSpan={6}
                    colSpan={8}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow hover>
                  <TableCell
                    style={{ textAlign: "center" }}
                    rowSpan={6}
                    colSpan={8}
                  >
                    No record
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <TableRow hover key={row._id + index}>
                    <TableCell style={styles}>{index + 1}</TableCell>

                    <TableCell style={styles} component="th" scope="row">
                      <b>
                        {row.businessType === "Private"
                          ? "Private"
                          : row.companyName || row.company}
                      </b>
                    </TableCell>

                    <TableCell style={styles}>{row.paymentRef}</TableCell>

                    <TableCell style={styles} align="left">
                      {moment(row?.paymentDate).format("DD/MM/YYYY")}
                    </TableCell>

                    <TableCell style={styles} align="left">
                      {row?.totalAmount?.toFixed(2)}
                    </TableCell>

                    <TableCell style={styles} align="left">
                      {row?.refundAmount?.toFixed(2)}
                    </TableCell>

                    <TableCell style={styles} align="left">
                      {moment(row?.refundDate).format("DD/MM/YYYY")}
                    </TableCell>

                    <TableCell style={styles} align="left">
                      {row?.notes}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default React.memo(CardItem);
